import styled from '@emotion/styled'
import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { MouseEvent, useCallback, useEffect } from 'react'

import Overlay from '@/components/Overlay'
import Portal from '@/components/Portal/Portal'
import { COLOR } from '@/styles/color'
import { fontSize } from '@/styles/mixin'
import { bodyScrollLock } from '@/utils/bodyScrollLock'
import { parseNoticePopupLink } from '@/utils/parse'

import useNoticePopup from '../../../hooks/useNoticePopup'

interface NoticePopupProps {
  isSearchOpen: boolean
}

function NoticePopup({ isSearchOpen }: NoticePopupProps) {
  const { isOpen, noticeDetail, handleClose, handleHideToday } = useNoticePopup()

  useEffect(() => {
    if (isOpen && TmapApp.env.isInApp && TmapApp.env.isAndroid) {
      TmapApp.handleBackKeyEventFromWeb({ handleFromWeb: true })
      const removeEventListener = TmapApp.utils.addNativeEventListener(
        'onHardwareBackKeyPressed',
        handleClose,
      )

      return () => {
        removeEventListener()
        TmapApp.handleBackKeyEventFromWeb({ handleFromWeb: false })
      }
    }
  }, [isOpen, handleClose])

  useEffect(() => {
    if (isOpen) {
      return bodyScrollLock()
    }
  }, [isOpen])

  const handleBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.currentTarget === e.target) {
        handleClose()
      }
    },
    [handleClose],
  )

  if (!isOpen || !noticeDetail || isSearchOpen) return null

  return (
    <Portal>
      <Backdrop onClick={handleBackdropClick}>
        <Container>
          <ScrollWrapper>
            <ContentArea>
              {noticeDetail.bannerImage && (
                <MainImage
                  src={noticeDetail.bannerImage}
                  alt=""
                />
              )}
              <Title>{noticeDetail.title}</Title>
              <Body
                dangerouslySetInnerHTML={{
                  __html: parseNoticePopupLink({ url: noticeDetail.contents }),
                }}
              />
            </ContentArea>
          </ScrollWrapper>
          <ButtonArea>
            <CloseButton onClick={handleClose}>닫기</CloseButton>
            <HideTodayButton onClick={handleHideToday}>오늘 하루 보지 않기</HideTodayButton>
          </ButtonArea>
        </Container>
      </Backdrop>
    </Portal>
  )
}

const Backdrop = styled(Overlay)`
  display: flex;
  align-items: flex-end;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: calc(100vh - 96px);
  padding-top: 20px;
  border-radius: 16px 16px 0 0;
  background-color: ${COLOR.gray.color.wb[0]};
  box-shadow: 0px -2px 2px rgba(0, 0, 0, 0.2);

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  animation: slideUp 0.6s cubic-bezier(0.215, 0.61, 0.355, 1) forwards;
`

const ScrollWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &::before,
  &::after {
    content: '';
    display: block;
    position: sticky;
    left: 0;
    width: 100%;
    height: 20px;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    top: 0;
    background: linear-gradient(180deg, #fff 10%, rgba(255, 255, 255, 0) 100%);
  }

  &::after {
    bottom: 0;
    background: linear-gradient(0deg, #fff 10%, rgba(255, 255, 255, 0) 100%);
  }
`

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 20px;
`

const MainImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
`

const Title = styled.h2`
  ${({ theme }) => fontSize(theme, 20)}
  font-weight: var(--font-weight-bold);
  color: ${COLOR.gray.color.gray[900]};
  word-break: break-word;
`

const Body = styled.div`
  ${({ theme }) => fontSize(theme, 16)}
  color: ${COLOR.gray.color.gray[500]};
  word-break: break-word;

  a {
    color: ${COLOR.primary.color.tmobi.blue[500]};
    text-decoration: underline;
  }
`

const ButtonArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 20px 16px;
  flex-shrink: 0;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border-radius: 8px;
  background-color: ${COLOR.primary.color.tmobi.blue[500]};
  ${({ theme }) => fontSize(theme, 18)}
  font-weight: var(--font-weight-medium);
  color: ${COLOR.gray.color.wb[0]};
`

const HideTodayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border-radius: 8px;
  ${({ theme }) => fontSize(theme, 18)}
  color: ${COLOR.gray.color.gray[900]};
`

export default NoticePopup
